/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { WaypointType } from 'src/graphql/generated/graphql.generated';
import { AddressRecord, AddressProps } from 'src/store/generated/address-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface WaypointProps {
  id: string;
  name: string;
  abbreviation: string;
  type: WaypointType;
  address: AddressProps;
  timezoneCd: string | null;
  isRotorWingLz: boolean | null;
  arrivalTime: Date | null;
  departureTime: Date | null;
  locationId: string | null;
  isPatientPickup: boolean | null;
  isPatientDropoff: boolean | null;
  arrivingType: WaypointType | null;
  estimatedDurationId: string | null;
  currentEtaId: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class WaypointRecord extends Record<WaypointProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  type: null as any,
  address: null as any,
  timezoneCd: null as any,
  isRotorWingLz: null as any,
  arrivalTime: null as any,
  departureTime: null as any,
  locationId: null as any,
  isPatientPickup: null as any,
  isPatientDropoff: null as any,
  arrivingType: null as any,
  estimatedDurationId: null as any,
  currentEtaId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    type: WaypointType;
    address: any;
    timezoneCd: string | null;
    isRotorWingLz: boolean | null;
    arrivalTime: string | number | Date | null;
    departureTime: string | number | Date | null;
    locationId: string | null;
    isPatientPickup: boolean | null;
    isPatientDropoff: boolean | null;
    arrivingType: WaypointType | null;
    estimatedDurationId: string | null;
    currentEtaId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): WaypointRecord {
    if (json == null) {
      throw new Error('WaypointRecord.fromJS: parameter cannot be null');
    }
    return new WaypointRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'WaypointJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'WaypointJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'WaypointJson.abbreviation'),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', WaypointType.Post),
        'WaypointJson.type',
      ),
      address: new AddressRecord(getFromOrThrow(json, (j) => j.address, 'WaypointJson.address')),
      timezoneCd: json.timezoneCd,
      isRotorWingLz: json.isRotorWingLz,
      arrivalTime: json.arrivalTime != null ? new Date(json.arrivalTime) : null,
      departureTime: json.departureTime != null ? new Date(json.departureTime) : null,
      locationId: json.locationId,
      isPatientPickup: json.isPatientPickup,
      isPatientDropoff: json.isPatientDropoff,
      arrivingType: json.arrivingType,
      estimatedDurationId: json.estimatedDurationId,
      currentEtaId: json.currentEtaId,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'WaypointJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    type: WaypointType;
    address: any;
    timezoneCd: string | null;
    isRotorWingLz: boolean | null;
    arrivalTime: string | number | Date | null;
    departureTime: string | number | Date | null;
    locationId: string | null;
    isPatientPickup: boolean | null;
    isPatientDropoff: boolean | null;
    arrivingType: WaypointType | null;
    estimatedDurationId: string | null;
    currentEtaId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [WaypointRecord, Error[]] {
    if (json == null) {
      throw new Error('WaypointRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new WaypointRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'WaypointJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'WaypointJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'WaypointJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', WaypointType.Post),
            'WaypointJson.type',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      address: tryOrDefault(
        () => new AddressRecord(getFromOrThrow(json, (j) => j.address, 'WaypointJson.address')),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      timezoneCd: tryOrDefault(
        () => json.timezoneCd,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isRotorWingLz: tryOrDefault(
        () => json.isRotorWingLz,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      arrivalTime: tryOrDefault(
        () => (json.arrivalTime != null ? new Date(json.arrivalTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      departureTime: tryOrDefault(
        () => (json.departureTime != null ? new Date(json.departureTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      locationId: tryOrDefault(
        () => json.locationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isPatientPickup: tryOrDefault(
        () => json.isPatientPickup,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isPatientDropoff: tryOrDefault(
        () => json.isPatientDropoff,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      arrivingType: tryOrDefault(
        () => json.arrivingType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      estimatedDurationId: tryOrDefault(
        () => json.estimatedDurationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      currentEtaId: tryOrDefault(
        () => json.currentEtaId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'WaypointJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: WaypointProps) {
    super(props);
    if (props == null) {
      throw new Error('WaypointRecord constructor parameter cannot be null');
    }
  }
}

export abstract class WaypointViewModelBase implements WaypointProps {
  protected readonly _props: WaypointProps;
  private _json?: WaypointProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get type(): WaypointType {
    return this._props.type;
  }
  get address(): AddressProps {
    return this._props.address;
  }
  get timezoneCd(): string | null {
    return this._props.timezoneCd;
  }
  get isRotorWingLz(): boolean | null {
    return this._props.isRotorWingLz;
  }
  get arrivalTime(): Date | null {
    return this._props.arrivalTime;
  }
  get departureTime(): Date | null {
    return this._props.departureTime;
  }
  get locationId(): string | null {
    return this._props.locationId;
  }
  get isPatientPickup(): boolean | null {
    return this._props.isPatientPickup;
  }
  get isPatientDropoff(): boolean | null {
    return this._props.isPatientDropoff;
  }
  get arrivingType(): WaypointType | null {
    return this._props.arrivingType;
  }
  get estimatedDurationId(): string | null {
    return this._props.estimatedDurationId;
  }
  get currentEtaId(): string | null {
    return this._props.currentEtaId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isTypeDropoffTeam() {
    return this._props.type === WaypointType.DropoffTeam;
  }
  public get isArrivingTypeDropoffTeam() {
    return this._props.arrivingType === WaypointType.DropoffTeam;
  }
  public get isTypeEnRouteDiversion() {
    return this._props.type === WaypointType.EnRouteDiversion;
  }
  public get isArrivingTypeEnRouteDiversion() {
    return this._props.arrivingType === WaypointType.EnRouteDiversion;
  }
  public get isTypeFuelBurn() {
    return this._props.type === WaypointType.FuelBurn;
  }
  public get isArrivingTypeFuelBurn() {
    return this._props.arrivingType === WaypointType.FuelBurn;
  }
  public get isTypeFuelLocation() {
    return this._props.type === WaypointType.FuelLocation;
  }
  public get isArrivingTypeFuelLocation() {
    return this._props.arrivingType === WaypointType.FuelLocation;
  }
  public get isTypeOther() {
    return this._props.type === WaypointType.Other;
  }
  public get isArrivingTypeOther() {
    return this._props.arrivingType === WaypointType.Other;
  }
  public get isTypePatientDropoffRendezvous() {
    return this._props.type === WaypointType.PatientDropoffRendezvous;
  }
  public get isArrivingTypePatientDropoffRendezvous() {
    return this._props.arrivingType === WaypointType.PatientDropoffRendezvous;
  }
  public get isTypePatientPickupRendezvous() {
    return this._props.type === WaypointType.PatientPickupRendezvous;
  }
  public get isArrivingTypePatientPickupRendezvous() {
    return this._props.arrivingType === WaypointType.PatientPickupRendezvous;
  }
  public get isTypePickupTeam() {
    return this._props.type === WaypointType.PickupTeam;
  }
  public get isArrivingTypePickupTeam() {
    return this._props.arrivingType === WaypointType.PickupTeam;
  }
  public get isTypePost() {
    return this._props.type === WaypointType.Post;
  }
  public get isArrivingTypePost() {
    return this._props.arrivingType === WaypointType.Post;
  }
  public get isTypePublicRelationsSite() {
    return this._props.type === WaypointType.PublicRelationsSite;
  }
  public get isArrivingTypePublicRelationsSite() {
    return this._props.arrivingType === WaypointType.PublicRelationsSite;
  }
  public get isTypeReceivingFacility() {
    return this._props.type === WaypointType.ReceivingFacility;
  }
  public get isArrivingTypeReceivingFacility() {
    return this._props.arrivingType === WaypointType.ReceivingFacility;
  }
  public get isTypeRendezvousPoint() {
    return this._props.type === WaypointType.RendezvousPoint;
  }
  public get isArrivingTypeRendezvousPoint() {
    return this._props.arrivingType === WaypointType.RendezvousPoint;
  }
  public get isTypeSceneLocation() {
    return this._props.type === WaypointType.SceneLocation;
  }
  public get isArrivingTypeSceneLocation() {
    return this._props.arrivingType === WaypointType.SceneLocation;
  }
  public get isTypeSendingFacility() {
    return this._props.type === WaypointType.SendingFacility;
  }
  public get isArrivingTypeSendingFacility() {
    return this._props.arrivingType === WaypointType.SendingFacility;
  }

  public isAnyType(...waypointType: WaypointType[]) {
    return waypointType.includes(this._props.type);
  }
  public isAnyArrivingType(...waypointType: WaypointType[]) {
    return this._props.arrivingType != null && waypointType.includes(this._props.arrivingType);
  }

  constructor(props: WaypointProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        type: this._props.type,
        address: this._props.address,
        timezoneCd: this._props.timezoneCd,
        isRotorWingLz: this._props.isRotorWingLz,
        arrivalTime: this._props.arrivalTime,
        departureTime: this._props.departureTime,
        locationId: this._props.locationId,
        isPatientPickup: this._props.isPatientPickup,
        isPatientDropoff: this._props.isPatientDropoff,
        arrivingType: this._props.arrivingType,
        estimatedDurationId: this._props.estimatedDurationId,
        currentEtaId: this._props.currentEtaId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllWaypointEntities = (state: AppState) => state.entityStores.waypoint.entities;

export const selectWaypointById = (id: string) => (state: AppState) =>
  state.entityStores.waypoint.entities.get(id);

export const selectWaypointMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.waypoint.meta.get(id);

export const selectWaypointAndMetadataById = (id: string) =>
  createSelector(selectWaypointById(id), selectWaypointMetadataById(id), (waypoint, meta) => ({
    waypoint,
    meta,
  }));

export function selectWaypointEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllWaypointEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, WaypointRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectWaypointEntitiesByName(name: string) {
  return createSelector(selectAllWaypointEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectWaypointEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllWaypointEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectWaypointEntitiesByType(type: WaypointType) {
  return createSelector(selectAllWaypointEntities, (entities) =>
    entities.filter((e) => e.type === type).valueSeq(),
  );
}

export function selectWaypointEntitiesByTimezoneCd(timezoneCd: string) {
  return createSelector(selectAllWaypointEntities, (entities) =>
    entities.filter((e) => e.timezoneCd === timezoneCd).valueSeq(),
  );
}

export function selectWaypointEntitiesByIsRotorWingLz(isRotorWingLz: boolean) {
  return createSelector(selectAllWaypointEntities, (entities) =>
    entities.filter((e) => e.isRotorWingLz === isRotorWingLz).valueSeq(),
  );
}

export function selectWaypointEntitiesByArrivalTime(arrivalTime: Date) {
  return createSelector(selectAllWaypointEntities, (entities) =>
    entities.filter((e) => e.arrivalTime === arrivalTime).valueSeq(),
  );
}

export function selectWaypointEntitiesByDepartureTime(departureTime: Date) {
  return createSelector(selectAllWaypointEntities, (entities) =>
    entities.filter((e) => e.departureTime === departureTime).valueSeq(),
  );
}

export function selectWaypointEntitiesByLocationId(locationId: string) {
  return createSelector(selectAllWaypointEntities, (entities) =>
    entities.filter((e) => e.locationId === locationId).valueSeq(),
  );
}

export function selectWaypointEntitiesByIsPatientPickup(isPatientPickup: boolean) {
  return createSelector(selectAllWaypointEntities, (entities) =>
    entities.filter((e) => e.isPatientPickup === isPatientPickup).valueSeq(),
  );
}

export function selectWaypointEntitiesByIsPatientDropoff(isPatientDropoff: boolean) {
  return createSelector(selectAllWaypointEntities, (entities) =>
    entities.filter((e) => e.isPatientDropoff === isPatientDropoff).valueSeq(),
  );
}

export function selectWaypointEntitiesByArrivingType(arrivingType: WaypointType) {
  return createSelector(selectAllWaypointEntities, (entities) =>
    entities.filter((e) => e.arrivingType === arrivingType).valueSeq(),
  );
}

export function selectWaypointEntitiesByEstimatedDurationId(estimatedDurationId: string) {
  return createSelector(selectAllWaypointEntities, (entities) =>
    entities.filter((e) => e.estimatedDurationId === estimatedDurationId).valueSeq(),
  );
}

export function selectWaypointEntitiesByCurrentEtaId(currentEtaId: string) {
  return createSelector(selectAllWaypointEntities, (entities) =>
    entities.filter((e) => e.currentEtaId === currentEtaId).valueSeq(),
  );
}

export function selectWaypointEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllWaypointEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
